import React, { useEffect, useState } from "react";
import { Button, Drawer, Tag, Typography } from "antd";
import { getFormattedAmount } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { PerformanceTag } from "Shared/PerformanceTag";
import { Tokens } from "Shared/Tokens";

const { Title, Text } = Typography;

const MarketView = (props) => {
    const {
        coinInfo = {},
        showMarket = false,
        setShowMarket = () => {},
        currency = "INR",
    } = props;

    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < 768 : false
    );

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const {
        price_change_24h,
        price_change_percentage_24h,
        high_24h,
        low_24h,
        ath,
        atl,
        ath_change_percentage,
        atl_change_percentage,
        total_volume,
        market_cap,
        circulating_supply,
        market_cap_rank,
        symbol = "",
    } = coinInfo;

    const currSymbol = getCurrencySymbol(currency);

    const rows = [
        {
            label: "Price change (24h)",
            value: (
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                    }}
                >
                    <span>
                        {currSymbol}&nbsp;
                        {getFormattedAmount(price_change_24h)}
                    </span>
                    {PerformanceTag(price_change_percentage_24h, true)}
                </span>
            ),
        },
        {
            label: "24h low",
            value: `${currSymbol} ${getFormattedAmount(low_24h)}`,
        },
        {
            label: "24h high",
            value: `${currSymbol} ${getFormattedAmount(high_24h)}`,
        },
        {
            label: "All-time high",
            value: (
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                    }}
                >
                    <span>
                        {currSymbol}&nbsp;{getFormattedAmount(ath)}
                    </span>
                    {PerformanceTag(ath_change_percentage, true)}
                </span>
            ),
        },
        {
            label: "All-time low",
            value: (
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                    }}
                >
                    <span>
                        {currSymbol}&nbsp;{getFormattedAmount(atl)}
                    </span>
                    {PerformanceTag(atl_change_percentage, true)}
                </span>
            ),
        },
        {
            label: "24h volume",
            value: `${currSymbol} ${getFormattedAmount(total_volume)}`,
        },
        {
            label: "Market cap",
            value: `${currSymbol} ${getFormattedAmount(market_cap)}`,
        },
        {
            label: "Circulating supply",
            value: getFormattedAmount(circulating_supply),
        },
    ];

    return (
        <div>
            <Drawer
                title={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "8px",
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                        }}
                    >
                        <Title
                            level={4}
                            style={{
                                margin: 0,
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                                fontSize: "17px",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {symbol.toUpperCase()} market statistics
                        </Title>
                        <Tag
                            style={{
                                margin: 0,
                                backgroundColor: Tokens.raised,
                                color: Tokens.textSecondary,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusTag,
                                fontFamily: Tokens.fontMono,
                                flexShrink: 0,
                            }}
                        >
                            Rank #{market_cap_rank}
                        </Tag>
                    </div>
                }
                placement={isMobile ? "bottom" : "right"}
                closable={false}
                push={false}
                onClose={() => setShowMarket(false)}
                height={isMobile ? "82%" : "100%"}
                width={isMobile ? "100%" : 460}
                open={showMarket}
                mask
                maskClosable
                maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
                headerStyle={{
                    backgroundColor: Tokens.bg,
                    borderBottom: `1px solid ${Tokens.hairline}`,
                    padding: "14px 16px",
                }}
                bodyStyle={{
                    backgroundColor: Tokens.bg,
                    padding: "20px 16px",
                }}
                footerStyle={{
                    backgroundColor: Tokens.card,
                    borderTop: `1px solid ${Tokens.hairline}`,
                    padding:
                        "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
                }}
                footer={
                    <div
                        style={{
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Button
                            type="default"
                            onClick={() => setShowMarket(false)}
                            style={{
                                height: "40px",
                                borderRadius: Tokens.radiusControl,
                                backgroundColor: Tokens.raised,
                                borderColor: Tokens.hairline,
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                            }}
                        >
                            Back
                        </Button>
                    </div>
                }
            >
                <div
                    style={{
                        maxWidth: Tokens.containerMaxWidth,
                        margin: "0 auto",
                        width: "100%",
                        backgroundColor: Tokens.card,
                        border: `1px solid ${Tokens.hairline}`,
                        borderRadius: Tokens.radiusCard,
                        padding: "8px 16px",
                    }}
                >
                    {rows.map((row, idx) => (
                        <div
                            key={row.label}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "12px",
                                borderBottom:
                                    idx < rows.length - 1
                                        ? `1px solid ${Tokens.hairline}`
                                        : "none",
                                padding: "12px 0",
                            }}
                        >
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                }}
                            >
                                {row.label}
                            </Text>
                            <Text
                                style={{
                                    color: Tokens.textPrimary,
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    textAlign: "right",
                                }}
                            >
                                {row.value}
                            </Text>
                        </div>
                    ))}
                </div>
            </Drawer>
        </div>
    );
};

export default MarketView;
